import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { settingsService } from '../services/settingsService';
import { useSettings } from './SettingsContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { themeSettings, isLoading: settingsLoading } = useSettings();
  const [theme, setTheme] = useState('light');
  const [isLoading, setIsLoading] = useState(true);

  // Load theme from centralized SettingsContext
  useEffect(() => {
    if (!settingsLoading && themeSettings) {
      if (themeSettings.theme) {
        setTheme(themeSettings.theme);
      }
      setIsLoading(false);
    }
  }, [themeSettings, settingsLoading]);

  // Apply theme to document root when theme changes
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const loadThemeFromSettings = async () => {
    // This is now only used for revert on error
    try {
      const response = await settingsService.getEffectiveSettings();
      if (response.success && response.data) {
        const generalSettings = response.data.find(cat => cat.category === 'GENERAL');
        if (generalSettings && generalSettings.settings.theme) {
          setTheme(generalSettings.settings.theme);
        }
      }
    } catch (error) {
      logger.warn('Could not load theme from settings, using default:', error);
    }
  };

  const applyThemeToDocument = (themeName) => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('theme-light', 'theme-dark', 'dark');
    
    // Add new theme class
    root.classList.add(`theme-${themeName}`);
    
    // Also add the standard 'dark' class for Tailwind CSS dark: prefixes
    if (themeName === 'dark') {
      root.classList.add('dark');
    }
    
    // Update meta theme-color for mobile browsers
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', themeName === 'dark' ? '#1f2937' : '#ffffff');
    }
  };

  const updateTheme = async (newTheme) => {
    try {
      setTheme(newTheme);
      
      // Save to settings
      await settingsService.updateSettingsBulk('general', {
        theme: newTheme
      });
    } catch (error) {
      logger.error('Failed to save theme setting:', error);
      // Revert theme on error
      await loadThemeFromSettings();
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    updateTheme(newTheme);
  };

  const value = {
    theme,
    setTheme: updateTheme,
    toggleTheme,
    isLoading,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;